import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  HoverableLinkSizes,
  styleConstants,
  Tag,
  TagColorSchemes,
  themeConstants,
} from '@paywithextend/components'
import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import Box from 'src/components/Box/Box'
import DeactivatedUserIndicator from 'src/components/DeactivatedUserIndicator/DeactivatedUserIndicator'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import OriginTooltip from 'src/components/OriginTooltip/OriginTooltip'
import TestId from 'src/components/TestId/TestId'
import { OrganizationRoles } from 'src/enums'
import { useFlyoutDispatch } from 'src/hooks'
import Trns from 'src/localization/Trns'
import { CreditCardType } from 'src/models/creditCard.models'
import {
  CreditCardPermissions,
  CreditCardPermissionsModel,
} from 'src/models/creditCardPermissions.models'
import { TransactionDetailModel } from 'src/models/transaction.models'
import { UserListModel } from 'src/models/user.models'
import { VirtualCardListModel } from 'src/models/virtualCard.models'
import {
  accountOverviewUrl,
  budgetOverviewUrl,
  peopleMembersDetailUrl,
  virtualCardDetailUrl,
} from 'src/navigation/urls'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import { getReceiptRules } from 'src/redux/actions/data/organizations.actions'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import {
  selectOrganizationReceiptMgmtEnabledFromState,
  selectUserExplicitOrganizationListModelFromState,
} from 'src/redux/selectors/data/organizations.selectors'
import styled, { css } from 'styled-components'

interface TransactionCardDetailsBoxProps {
  className?: string
  transaction: TransactionDetailModel
  virtualCard?: VirtualCardListModel
  creditCardPermissions?: CreditCardPermissionsModel
  parentCreditCardPermissions?: CreditCardPermissionsModel
  // creditCard?: CreditCardListModel
  cardUser?: UserListModel
  cardCreator?: UserListModel
}

const InfoStackDiv = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 20px;
`

const InfoHeaderSpan = styled.span`
  margin-bottom: 6px;
  ${getFontCss(
    12,
    styleConstants.colors.gray_darkest,
    styleConstants.fontFamilies.regular,
  )};
  display: flex;
  align-items: center;
`

const InfoBodySpan = styled.span`
  ${getFontCss(
    16,
    themeConstants.colors.font_primary,
    styleConstants.fontFamilies.regular,
  )};
  text-overflow: ellipsis;
`

const StyledHoverableLink = styled(HoverableLink)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
`

const TransactionCardDetailsBox: React.FC<TransactionCardDetailsBoxProps> = ({
  className,
  transaction,
  virtualCard,
  creditCardPermissions,
  parentCreditCardPermissions,
  // creditCard,
  cardUser,
  cardCreator,
}): React.ReactElement => {
  const orgReceiptRulesEnabled = useSelector(
    selectOrganizationReceiptMgmtEnabledFromState,
  )
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const user = useSelector(selectUserListModelFromState)
  const { data: orgFeatureFlags } = useQueryOrganizationFeatureFlags({
    orgId: user?.organizationId,
  })
  const showGuestRole = orgFeatureFlags?.guestRoleEnabled
  const isGuestUser =
    showGuestRole && user?.organizationRole === OrganizationRoles.guest

  const { analytics } = useAnalyticsContext()

  const canReadCreditCard = creditCardPermissions?.canRead
  const canReadParentCreditCard = parentCreditCardPermissions?.canRead

  const dispatch = useFlyoutDispatch()

  useEffect((): void => {
    if (
      organization?.id !== undefined &&
      organization?.receiptRule === undefined &&
      orgReceiptRulesEnabled
    ) {
      dispatch(getReceiptRules(organization.id))
    }
  }, [organization?.id, orgReceiptRulesEnabled])

  // If the card does not have IssueDelegateCreditCards - it is a budget
  const isBudget = !creditCardPermissions?.permissions.includes(
    CreditCardPermissions.IssueDelegateCreditCards,
  )
  return (
    <Box
      className={className}
      css={css`
        display: flex;
        flex-direction: column;
        background-color: ${styleConstants.colors.gray_lightest};
        margin-bottom: 60px;
        ${getBreakpointDown(breakpointSizes.XSM)(css`
          margin-bottom: 100px;
        `)}
      `}
    >
      <span
        css={css`
          ${getFontCss(
            16,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.medium,
          )}
          margin-bottom: 40px;
          ${getBreakpointDown(breakpointSizes.XSM)(css`
            margin-bottom: 30px;
          `)}
        `}
      >
        <Trns source={'Card Details'} />
      </span>
      <div
        css={css`
          display: grid;
          grid-template-columns: 50% 50%;
        `}
      >
        <InfoStackDiv>
          <InfoHeaderSpan>
            <Trns source={'Card'} />
          </InfoHeaderSpan>
          {transaction.virtualCardId !== undefined && (
            <StyledHoverableLink
              to={virtualCardDetailUrl(transaction.virtualCardId)}
              size={HoverableLinkSizes.Large}
              underline
              ariaLabel={transaction.vcnDisplayName}
            >
              {transaction.vcnDisplayName}
            </StyledHoverableLink>
          )}
          {transaction.virtualCardId === undefined && (
            <InfoBodySpan>{transaction.vcnDisplayName}</InfoBodySpan>
          )}
        </InfoStackDiv>
        <InfoStackDiv>
          <InfoHeaderSpan>
            <Trns source={'Card User'} />
          </InfoHeaderSpan>
          <InfoBodySpan>
            {cardUser !== undefined && transaction.nameOnCard !== undefined ? (
              <div
                css={css`
                  display: flex;
                  flex-direction: row;
                `}
              >
                <div
                  onClick={(): void => {
                    analytics?.track(
                      'transaction detail card details - open user detail - text press',
                      {
                        screen: `/transactions/${cardUser}`,
                        interaction: 'text press',
                        intent: 'open user detail',
                      },
                    )
                  }}
                  onKeyPress={(): null => {
                    return null
                  }}
                  role="button"
                  tabIndex={0}
                  css={css`
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                  `}
                >
                  <StyledHoverableLink
                    to={peopleMembersDetailUrl(cardUser?.id)}
                    size={HoverableLinkSizes.Large}
                    underline
                    ariaLabel={transaction.nameOnCard}
                  >
                    {transaction.nameOnCard}
                  </StyledHoverableLink>
                </div>
                {cardUser?.deactivated && <DeactivatedUserIndicator />}
              </div>
            ) : (
              transaction.nameOnCard
            )}
          </InfoBodySpan>
        </InfoStackDiv>
        {transaction.creditCardId !== undefined && (
          <>
            {canReadCreditCard && (
              <InfoStackDiv>
                <InfoHeaderSpan>
                  <Trns source={'Created with'} />
                </InfoHeaderSpan>
                <StyledHoverableLink
                  to={
                    isBudget
                      ? budgetOverviewUrl(transaction.creditCardId)
                      : accountOverviewUrl(transaction.creditCardId)
                  }
                  size={HoverableLinkSizes.Large}
                  underline
                  ariaLabel={transaction.creditCardDisplayName}
                >
                  {transaction.creditCardDisplayName}
                </StyledHoverableLink>
              </InfoStackDiv>
            )}
            {transaction.cardholderName !== undefined && (
              <InfoStackDiv>
                <InfoHeaderSpan>
                  <Trns source={'Card Creator'} />
                </InfoHeaderSpan>
                <InfoBodySpan>
                  {cardCreator !== undefined &&
                  transaction.cardholderName !== undefined ? (
                    <div
                      css={css`
                        display: flex;
                        flex-direction: row;
                      `}
                    >
                      <div
                        onClick={(): void => {
                          analytics?.track(
                            'transaction detail card details - open user detail - text press',
                            {
                              screen: `/transactions/${cardCreator}`,
                              interaction: 'text press',
                              intent: 'open user detail',
                            },
                          )
                        }}
                        onKeyPress={(): null => {
                          return null
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        {isGuestUser ? (
                          <span
                            css={css`
                              ${getFontCss(
                                16,
                                themeConstants.colors.font_primary,
                                styleConstants.fontFamilies.regular,
                              )}
                            `}
                          >
                            {transaction.cardholderName}
                          </span>
                        ) : (
                          <StyledHoverableLink
                            to={peopleMembersDetailUrl(cardCreator?.id)}
                            size={HoverableLinkSizes.Large}
                            underline
                            ariaLabel={transaction.cardholderName}
                          >
                            {transaction.cardholderName}
                          </StyledHoverableLink>
                        )}
                      </div>
                      {cardCreator?.deactivated && <DeactivatedUserIndicator />}
                    </div>
                  ) : (
                    transaction.cardholderName
                  )}
                </InfoBodySpan>
              </InfoStackDiv>
            )}
          </>
        )}
        {canReadParentCreditCard &&
          transaction.creditCardType === CreditCardType.Delegate &&
          transaction?.parentCreditCardDisplayName !== undefined &&
          transaction?.parentCreditCardId !== undefined && (
            <InfoStackDiv>
              <InfoHeaderSpan>
                <Trns source={'Parent Account'} />
              </InfoHeaderSpan>
              <InfoBodySpan>
                <StyledHoverableLink
                  to={accountOverviewUrl(transaction.parentCreditCardId)}
                  size={HoverableLinkSizes.Large}
                  underline
                  ariaLabel={transaction.parentCreditCardDisplayName}
                >
                  {transaction.parentCreditCardDisplayName}
                </StyledHoverableLink>
              </InfoBodySpan>
            </InfoStackDiv>
          )}
        {canReadCreditCard && organization?.physicalClearingsEnabled && (
          <InfoStackDiv>
            <InfoHeaderSpan>
              <Trns source={'Origin'} />
              <OriginTooltip openLeft={false} />
            </InfoHeaderSpan>
            <InfoBodySpan>{transaction.sourceLabel}</InfoBodySpan>
          </InfoStackDiv>
        )}
        {virtualCard !== undefined &&
          !virtualCard.direct &&
          orgReceiptRulesEnabled &&
          organization?.receiptRule?.enabled && (
            <InfoStackDiv>
              <InfoHeaderSpan>
                <Trns source={'Receipt Rules'} />
              </InfoHeaderSpan>
              <InfoBodySpan>
                <TestId testId={'transactionFlyoutReceiptTag'}>
                  {!virtualCard.receiptRulesExempt ? (
                    <Tag
                      text={<Trns source={'Receipts Required'} />}
                      color={TagColorSchemes.Green}
                    />
                  ) : (
                    <Tag
                      text={<Trns source={'Receipts Not Required'} />}
                      color={TagColorSchemes.Default}
                    />
                  )}
                </TestId>
              </InfoBodySpan>
            </InfoStackDiv>
          )}
      </div>
    </Box>
  )
}

export default TransactionCardDetailsBox
